import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Search } from "lucide-react";
import QuickExit from "./QuickExit";
import SearchModal from "./SearchModal";

const NAV_ITEMS = [
  { to: "/understanding", label: "Understanding the system" },
  { to: "/glossary", label: "Glossary" },
  { to: "/support", label: "Find support" },
  { to: "/campaign", label: "Our campaign" },
  { to: "/stories", label: "Stories" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

function Logo({ onClick }) {
  return (
    <Link to="/" data-testid="site-logo-link" className="group" onClick={onClick}>
      <div className="flex flex-col leading-none">
        <span className="text-xs uppercase tracking-[0.22em] text-ember font-accent">
          A space for families
        </span>
        <span className="font-serif text-2xl md:text-[1.65rem] text-ink mt-1 tracking-tight">
          You Are Not Alone
        </span>
      </div>
    </Link>
  );
}

function DesktopNav() {
  return (
    <nav className="hidden lg:flex items-center gap-7">
      {NAV_ITEMS.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          data-testid={`nav-${n.to.replace("/", "")}-link`}
          className={({ isActive }) =>
            `text-sm font-accent tracking-wide transition-colors ${
              isActive ? "text-river" : "text-ink-muted hover:text-ink"
            }`
          }
        >
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function MobileMenu({ onClose }) {
  return (
    <div data-testid="mobile-menu" className="lg:hidden border-t border-rule bg-linen">
      <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
        {NAV_ITEMS.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onClose}
            data-testid={`mobile-nav-${n.to.replace("/", "")}-link`}
            className={({ isActive }) =>
              `py-3 px-2 text-base font-accent border-b border-rule/60 ${
                isActive ? "text-river" : "text-ink-muted"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Global keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-linen/95 backdrop-blur-md border-b border-rule"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-4 flex items-center justify-between gap-4">
        <Logo onClick={() => setOpen(false)} />
        <div className="flex items-center gap-3 md:gap-5">
          <DesktopNav />
          <button
            type="button"
            data-testid="search-open-button"
            aria-label="Search the site"
            onClick={() => setSearchOpen(true)}
            className="text-ink-muted hover:text-ink p-2 -mx-1 transition-colors"
          >
            <Search strokeWidth={1.5} size={18} />
          </button>
          <QuickExit />
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-ink p-2 -mr-2"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X strokeWidth={1.5} size={22} /> : <Menu strokeWidth={1.5} size={22} />}
          </button>
        </div>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default Header;
